import { useEffect, useLayoutEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { meQuery } from '@/shared/api/auth';

/**
 * Keyboard shortcuts ("Tezkor tugmalar", 03-modules §7.3).
 *
 * Defaults live in the catalog below; a user's changes are stored as overrides only:
 * `users.settings.hotkeys = { [actionId]: "Ctrl+Shift+D" }` (an empty string disables the action).
 *
 *   useHotkey('save', () => form.submit());                 // fires on the user's combo for "Saqlash"
 *   useHotkey('create', openPanel, { enabled: canCreate });
 *
 * Combos are written `Ctrl+Shift+D`: modifiers in the order Ctrl, Alt, Shift, then one key. "Ctrl" also matches
 * ⌘ on macOS. Letters and digits are matched by physical key (layout independent: Ctrl+S works with a Cyrillic
 * layout too); `+` ignores Shift (it needs Shift on most layouts) and `=` counts as `+`.
 */

export const HOTKEY_GROUPS = ['general', 'drawing', 'table'] as const;
export type HotkeyGroup = (typeof HOTKEY_GROUPS)[number];

export interface HotkeyAction {
  id: string;
  group: HotkeyGroup;
  /** default combo (design 556:16851) */
  defaultCombo: string;
}

/** Catalog in design order. Labels/descriptions are i18n keys of the `settings` namespace: `hotkeys.actions.<id>`. */
export const HOTKEY_ACTIONS = [
  { id: 'create', group: 'general', defaultCombo: 'Ctrl+N' },
  { id: 'save', group: 'general', defaultCombo: 'Ctrl+S' },
  { id: 'search', group: 'general', defaultCombo: 'Ctrl+K' },
  { id: 'cancel', group: 'general', defaultCombo: 'Esc' },
  { id: 'refresh', group: 'general', defaultCombo: 'F5' },
  { id: 'help', group: 'general', defaultCombo: 'Shift+?' },
  { id: 'zoomIn', group: 'drawing', defaultCombo: 'Ctrl++' },
  { id: 'zoomOut', group: 'drawing', defaultCombo: 'Ctrl+-' },
  { id: 'addDevice', group: 'drawing', defaultCombo: 'D' },
  { id: 'layers', group: 'drawing', defaultCombo: 'L' },
  { id: 'export', group: 'table', defaultCombo: 'Ctrl+E' },
  { id: 'copy', group: 'table', defaultCombo: 'Ctrl+C' },
  { id: 'selectAll', group: 'table', defaultCombo: 'Ctrl+A' },
  { id: 'saveChart', group: 'table', defaultCombo: 'Ctrl+G' },
] as const satisfies readonly HotkeyAction[];

export type HotkeyActionId = (typeof HOTKEY_ACTIONS)[number]['id'];
export type HotkeyOverrides = Readonly<Record<string, string>>;

const ACTIONS_BY_ID: ReadonlyMap<string, HotkeyAction> = new Map(HOTKEY_ACTIONS.map((a) => [a.id, a]));
export const hotkeyAction = (id: string): HotkeyAction | undefined => ACTIONS_BY_ID.get(id);

/** Browser shortcuts that cannot (or must not) be taken over — rejected when capturing. */
export const RESERVED_COMBOS: readonly string[] = ['Ctrl+W', 'Ctrl+T', 'Ctrl+Tab', 'Ctrl+Shift+W', 'Ctrl+Shift+T', 'Ctrl+Shift+Tab'];

/* ───────────────────────── combo strings ───────────────────────── */

export interface ParsedCombo {
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  key: string;
}

const MODIFIERS = ['Ctrl', 'Alt', 'Shift'] as const;

/** "Ctrl++" → { ctrl, key: "+" }; "Shift+?" → { shift, key: "?" }. Returns null for an empty / malformed combo. */
export function parseCombo(combo: string | null | undefined): ParsedCombo | null {
  if (!combo) return null;
  let rest = combo.trim();
  const out: ParsedCombo = { ctrl: false, alt: false, shift: false, key: '' };
  for (;;) {
    const m = /^(Ctrl|Alt|Shift)\+(.+)$/.exec(rest);
    if (!m) break;
    if (m[1] === 'Ctrl') out.ctrl = true;
    else if (m[1] === 'Alt') out.alt = true;
    else out.shift = true;
    rest = m[2]!;
  }
  out.key = rest === '=' ? '+' : rest;
  if (out.key === '+') out.shift = false;
  return out.key ? out : null;
}

export function formatCombo(c: ParsedCombo): string {
  return [c.ctrl && 'Ctrl', c.alt && 'Alt', c.shift && 'Shift', c.key].filter(Boolean).join('+');
}

/** Normalise a stored combo (modifier order, `=` → `+`). */
export const normalizeCombo = (combo: string | null | undefined) => {
  const parsed = parseCombo(combo);
  return parsed ? formatCombo(parsed) : '';
};

/** Key caps for display: "Ctrl+Shift+D" → ["Ctrl", "Shift", "D"]. */
export function comboKeys(combo: string | null | undefined): string[] {
  const c = parseCombo(combo);
  if (!c) return [];
  return [...MODIFIERS.filter((m) => (m === 'Ctrl' ? c.ctrl : m === 'Alt' ? c.alt : c.shift)), c.key];
}

const KEY_NAMES: Record<string, string> = {
  Escape: 'Esc',
  ' ': 'Space',
  Spacebar: 'Space',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  '=': '+',
  Add: '+',
  Subtract: '-',
};

const isModifierKey = (key: string) => key === 'Control' || key === 'Shift' || key === 'Alt' || key === 'Meta' || key === 'AltGraph' || key === 'OS';

/** The combo of a keyboard event, or null for a lone modifier / dead key. */
export function eventToCombo(e: Pick<KeyboardEvent, 'key' | 'code' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>): string | null {
  if (!e.key || isModifierKey(e.key) || e.key === 'Dead' || e.key === 'Unidentified') return null;
  let key: string;
  const letter = /^Key([A-Z])$/.exec(e.code);
  const digit = /^(?:Digit|Numpad)(\d)$/.exec(e.code);
  if (letter) key = letter[1]!;
  else if (digit) key = digit[1]!;
  else if (e.code === 'NumpadAdd') key = '+';
  else if (e.code === 'NumpadSubtract') key = '-';
  else key = KEY_NAMES[e.key] ?? (e.key.length === 1 ? e.key.toUpperCase() : e.key);
  // `+` needs Shift on most layouts — "Ctrl + +" means the plus key, with or without Shift
  const shift = key === '+' ? false : e.shiftKey;
  return formatCombo({ ctrl: e.ctrlKey || e.metaKey, alt: e.altKey, shift, key });
}

/* ───────────────────────── resolution & conflicts ───────────────────────── */

/** The combo in effect for an action: the user's override (empty = disabled) or the default. */
export function resolveCombo(actionId: string, overrides: HotkeyOverrides | null | undefined): string {
  const action = ACTIONS_BY_ID.get(actionId);
  if (!action) return '';
  const own = overrides && Object.hasOwn(overrides, actionId) ? overrides[actionId] : undefined;
  return normalizeCombo(own ?? action.defaultCombo);
}

export const isModified = (actionId: string, overrides: HotkeyOverrides | null | undefined) => {
  const action = ACTIONS_BY_ID.get(actionId);
  return Boolean(action) && resolveCombo(actionId, overrides) !== normalizeCombo(action!.defaultCombo);
};

/** Groups whose shortcuts are active at the same time: "Umumiy" everywhere, "Chizma" and "Jadval" never together. */
export const groupsOverlap = (a: HotkeyGroup, b: HotkeyGroup) => a === b || a === 'general' || b === 'general';

/** Action ids whose combo clashes with another action of an overlapping group ("Takrorlanadi"). */
export function findConflicts(combos: Readonly<Record<string, string>>): Set<string> {
  const out = new Set<string>();
  const list = HOTKEY_ACTIONS.map((a) => ({ ...a, combo: combos[a.id] ?? '' })).filter((a) => a.combo);
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const a = list[i]!;
      const b = list[j]!;
      if (a.combo === b.combo && groupsOverlap(a.group, b.group)) {
        out.add(a.id);
        out.add(b.id);
      }
    }
  }
  return out;
}

/** Number of clashing pairs (the "1 ta takrorlanish" badge). */
export function countConflictPairs(combos: Readonly<Record<string, string>>): number {
  let n = 0;
  const list = HOTKEY_ACTIONS.map((a) => ({ ...a, combo: combos[a.id] ?? '' })).filter((a) => a.combo);
  for (let i = 0; i < list.length; i++)
    for (let j = i + 1; j < list.length; j++)
      if (list[i]!.combo === list[j]!.combo && groupsOverlap(list[i]!.group, list[j]!.group)) n++;
  return n;
}

/* ───────────────────────── runtime ───────────────────────── */

/** While the "Tezkor tugmalar" page records a combo, no shortcut fires. */
let captureDepth = 0;
export function beginHotkeyCapture(): () => void {
  captureDepth++;
  let done = false;
  return () => {
    if (!done) {
      done = true;
      captureDepth = Math.max(0, captureDepth - 1);
    }
  };
}

const EDITABLE = 'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="textbox"], [role="combobox"]';

function inEditable(target: EventTarget | null): boolean {
  const el = target instanceof Element ? target : null;
  if (!el) return false;
  const input = el.closest(EDITABLE);
  if (!input) return false;
  if (input instanceof HTMLInputElement) return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(input.type);
  return true;
}

/** The current user's overrides (live: follows `useUpdateSettings`). */
export function useHotkeyOverrides(): HotkeyOverrides {
  const { data } = useQuery(meQuery);
  return data?.user.settings.hotkeys ?? {};
}

/** The combo in effect for an action, for hints ("Saqlash (Ctrl+S)"). */
export function useHotkeyCombo(actionId: HotkeyActionId): string {
  return resolveCombo(actionId, useHotkeyOverrides());
}

export interface UseHotkeyOptions {
  /** listen only while true (default true) */
  enabled?: boolean;
  /**
   * fire while focus is in a text field. Default: only combos with Ctrl/Alt or function keys (F1…F12, Esc) fire
   * there, so typing "L" in a field never toggles layers.
   */
  allowInInputs?: boolean;
  /** call `preventDefault()` on a match (default true) */
  preventDefault?: boolean;
}

/**
 * Runs `handler` when the user's combo for `actionId` is pressed. Several components may listen to the same action;
 * the handler always sees its latest props.
 */
export function useHotkey(actionId: HotkeyActionId, handler: (event: KeyboardEvent) => void, options: UseHotkeyOptions = {}) {
  const combo = useHotkeyCombo(actionId);
  const { enabled = true, allowInInputs, preventDefault = true } = options;
  const ref = useRef(handler);
  useLayoutEffect(() => {
    ref.current = handler;
  });
  useEffect(() => {
    if (!enabled || !combo) return;
    const parsed = parseCombo(combo)!;
    const safeInInputs = allowInInputs ?? (parsed.ctrl || parsed.alt || /^(F\d{1,2}|Esc)$/.test(parsed.key));
    const onKeyDown = (e: KeyboardEvent) => {
      if (captureDepth > 0 || e.defaultPrevented || e.isComposing) return;
      if (eventToCombo(e) !== combo) return;
      if (!safeInInputs && inEditable(e.target)) return;
      if (preventDefault) e.preventDefault();
      ref.current(e);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [combo, enabled, allowInInputs, preventDefault]);
}
